import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import Empty from 'src/common/components/Empty';
import { LanguageKeys } from 'src/common/constants/languages';
import { ICompetition } from 'src/interfaces/Competition';
import { IEdition } from 'src/interfaces/Edition';
import DistanceInsightsTabs from 'src/pages/Dashboard/Insights/components/DistanceInsightsTabs';
import InsightsPanelSurface from 'src/pages/Dashboard/Insights/components/InsightsPanelSurface';
import ParticipantsDistributionMap from 'src/pages/PublicPages/components/ParticipantsDistributionMap';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  edition: IEdition;
  competition: ICompetition;
  distances: string[];
  currentLang: LanguageKeys;
  showStatisticsBody: boolean;
  audience?: 'public' | 'admin';
}

export default function EditionInsightsDetail({
  edition,
  competition,
  distances,
  currentLang,
  showStatisticsBody,
  audience = 'admin',
}: Props) {
  const { t } = useTranslation();
  const { activeEditionId, competitionsById } = useSystemOptionsStore(s => s);

  const isCurrentEdition = edition.id === activeEditionId;
  const hasStatisticsBody = showStatisticsBody && edition.statistics;

  const emptyTitle =
    audience === 'public'
      ? isCurrentEdition
        ? t('race.results.noStatsYetTitle')
        : t('race.results.noStatsPastTitle', { year: edition.year })
      : t('statistics.notEnoughData');

  const emptyDescription =
    audience === 'public'
      ? isCurrentEdition
        ? t('race.results.noStatsYetDescription')
        : t('race.results.noStatsPastDescription')
      : t('insights.noSavedData');

  const competitionsForEdition = (edition.competitions ?? [])
    .map(ec => competitionsById?.[ec.competitionId])
    .filter((c): c is ICompetition => Boolean(c));

  if (distances.length === 0) {
    return (
      <Typography variant="body1" sx={{ my: 2 }}>
        {t('insights.competitionNotInEdition', {
          competition: competition.i18n[currentLang],
          year: edition.year,
        })}
      </Typography>
    );
  }

  return (
    <>
      <ParticipantsDistributionMap
        editionId={edition.id}
        editionYear={edition.year}
        hasResultsData={Boolean(edition.statistics)}
        competitions={competitionsForEdition}
        currentLang={currentLang}
      />
      <Box sx={{ my: 2 }}>
        {hasStatisticsBody ? (
          <DistanceInsightsTabs
            competitionId={competition.id}
            distances={distances}
            statistics={edition.statistics}
            currentLang={currentLang}
          />
        ) : (
          <InsightsPanelSurface>
            <Empty
              embedded
              icon={<BarChartOutlinedIcon fontSize="large" />}
              title={emptyTitle}
              description={emptyDescription}
            />
          </InsightsPanelSurface>
        )}
      </Box>
    </>
  );
}
